import type { AiCapabilityPage } from "../schema.js";
import { LIVE, ALL_ROLES } from "./_shared.js";

/**
 * `JOIN-REQUEST-MINE` (@UC0216) — trang **redirect** (US-AIV2-0049 vòng 3, QĐ điều phối 17/09):
 * việc của **CHÍNH MÌNH** — rút lại lời xin vào nhóm — tách khỏi `GRP-JOIN-REQUEST` (việc QUẢN TRỊ).
 *
 * 🔴 VÌ SAO TÁCH: `ai-capability-gate.service.ts` lấy chính `page.roles` làm cổng. Khi `GRP-JOIN-REQUEST`
 * hẹp còn `OWNER_ADMIN` (review #827 HIGH-2 — màn của nó là danh sách quản trị), cửa tự-phục-vụ
 * `DELETE /users/me/groups/{groupId}/join-request` đi theo trang đó sẽ **mất lối vào bằng chat** cho đúng
 * người cần nó. Một PR có mục tiêu "0 thủng" mà để hở lỗ này là tự mâu thuẫn.
 *
 * ⚠️ HAI ĐIỂM LỆCH so với đề bài điều phối — ĐO ĐƯỢC, không phải tự ý đổi:
 *  1. **Không khai `GET /me/join-requests`.** Cửa đó CÓ ở `apps/api` (`my-invitations.controller.ts:47`,
 *     `@Controller("me")`) nhưng **`apps/mobile` KHÔNG gọi** — nó không nằm trong 107 cửa của bản đồ.
 *     Khai vào sẽ thành `staleClaims` ("trang khai cửa mobile không còn gọi") ⇒ gate G14f ĐỎ.
 *  2. **`screen: "/groups"`, không phải màn "lời mời & yêu cầu của tôi".** Màn đó KHÔNG TỒN TẠI dưới dạng
 *     route: `PendingGroupScreen` mở bằng `Navigator.push(MaterialPageRoute…)` từ `groups_feature.dart:767`
 *     — một màn **không có URL**, nên không thể là đích của `renderRedirect` (và cây GoRoute chỉ có 21 route).
 *     Trỏ `/groups` là nơi người dùng THẬT SỰ tới: bấm vào nhóm đang chờ trong danh sách.
 *     Câu chữ nói đúng bước đó, không hứa một màn không có (bài học M3: *đừng hứa màn cụ thể khi nút mở màn chung*).
 */
export const JOIN_REQUEST_MINE = {
  id: "JOIN-REQUEST-MINE",
  uc: "@UC0216",
  tier: "redirect",
  status: LIVE,
  // Mọi vai: đây là việc của CHÍNH người dùng với nhóm họ đang chờ — cửa `users/me/...` không gác vai
  // (`my-groups.controller.ts:74`), nên không vai nào bị mời vào ngõ cụt.
  roles: ALL_ROLES,
  flags: [],
  slots: [],
  llm: {
    description: "Rút lại lời xin vào nhóm của chính bạn, hoặc xem những nhóm bạn đang chờ được duyệt.",
    examples: [
      "huỷ xin vào nhóm",
      "huy xin vao nhom",
      "rút lại lời xin vào nhóm",
      "rut lai loi xin vao nhom",
      "tôi đang chờ duyệt ở nhóm nào",
      // ——— 0105: thêm cho đủ 40 ———
      "huỷ lời xin vào nhóm của tôi",
      "huy loi xin vao nhom cua toi",
      "rút đơn xin vào nhóm",
      "rut don xin vao nhom",
      "tôi không muốn vào nhóm đó nữa",
      "toi khong muon vao nhom do nua",
      "bỏ yêu cầu tham gia nhóm",
      "bo yeu cau tham gia nhom",
      "xin vào nhầm nhóm, rút lại giúp",
      "xin vao nham nhom rut lai giup",
      "huỷ yêu cầu vào nhóm",
      "huy yeu cau vao nhom",
      "tôi xin vào nhóm rồi mà muốn rút",
      "toi xin vao nhom roi ma muon rut",
      "rút lời xin tham gia",
      "rut loi xin tham gia",
      "gỡ đơn xin vào nhóm của mình",
      "go don xin vao nhom cua minh",
      "thôi không xin vào nhóm nữa",
      "thoi khong xin vao nhom nua",
      "huỷ đăng ký vào nhóm",
      "huy dang ky vao nhom",
      "bỏ xin vào",
      "bo xin vao",
      "rút lại đơn tham gia nhóm lớp",
      "rut lai don tham gia nhom lop",
      "tôi lỡ bấm xin vào nhóm, huỷ giúp",
      "toi lo bam xin vao nhom huy giup",
      "xoá yêu cầu xin vào nhóm của tôi",
      "xoa yeu cau xin vao nhom cua toi",
      "huỷ xin tham gia nhóm đội bóng",
      "huy xin tham gia nhom doi bong",
      "rút xin vào nhóm",
      "rut xin vao nhom",
      "tôi muốn rút lại yêu cầu tham gia",
      "toi muon rut lai yeu cau tham gia",
      // 🔴 US-AIV2-0112 lô Sổ 2 (NEED C07 · @UC0216) — người ĐANG CHỜ DUYỆT hỏi "sao chưa vào được nhóm" ⇒ 3/3 "chưa rõ ý" (probe `dung`
      // 29/09: trang này không vào top-8 BM25 — hạng 15). Cách nói CHỌN BẰNG ĐO top-8 OFFLINE toàn bộ golden-set (`_runs/US-AIV2-0112/SO-LO2`):
      // KHÔNG dùng chữ "chưa" (kéo READ-COLLECTIONS#38/39 "đợt thu nào chưa xong" đổi tập top-8 — IDF của "chua" dịch) và KHÔNG "tới đâu
      // rồi"/"đâu"/"rồi" (C07g: kéo trang này vào top-8 câu READ-COLLECTIONS#06 "quy thu toi dau roi" ⇒ lật sang READ-FUND-MONEY). Câu probe
      // ("sao mình chưa vào được nhóm lớp 2A") KHÁC ví dụ để số đo không tự trả lời chính nó.
      "sao không vào được nhóm",
      "sao khong vao duoc nhom",
    ],
  },
  screen: "/groups",
  screenShows: "my_join_request",
  endpoints: ["DELETE /users/me/groups/{groupId}/join-request"],
  facts: [],
  templates: {
    // US-AIV2-0111 C07g: câu đúng cho CẢ hai ý (hỏi "sao chưa vào được" và "rút lời xin") — lời xin cần chủ nhóm/quản trị duyệt; nút mở
    // danh sách. Template KHÔNG vào prompt/chỉ mục BM25 ⇒ không đổi bản ghi eval.
    redirect:
      "Lời xin vào nhóm cần chủ nhóm hoặc quản trị viên duyệt. Mình mở danh sách nhóm của bạn nhé — nhóm đang chờ duyệt có nhãn riêng; bấm vào đó để xem hoặc rút lời xin.",
  },
  journey: { after: ["GRP-OPEN"] },
  help: { title: "Rút lời xin vào nhóm", tryNow: "huỷ xin vào nhóm" },
} satisfies AiCapabilityPage;
